import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useMe } from '../lib/me';
import { DeadlineNotice } from './CloseDeadlineAlert';
import { LatePayButton } from './LatePay';

/**
 * 팀별 마감 기한 한 줄(정산 › 마감 띠 아래 · 11-04 way) — 영업 · 월정기 · 회계.
 * 자동 = 매달 1일 0시 10분 영업일 기준으로 들어간다(영업 3번째 · 월정기 5번째 영업일 · 회계 12일 · 주말·공휴일이면 다음 영업일).
 * 고치기·풀기 = 관리자만(「이슈 있으면 내가 수동으로 일자 조정」). 강제는 서버 `saveCloseDeadline`.
 */
const KINDS = ['영업', '월정기', '회계'] as const;
type Row = { driveMon: string; kind: string; deadline: string; note: string | null; closed: boolean };

const md = (d: string) => `${Number(d.slice(5, 7))}/${Number(d.slice(8, 10))}`;

export function CloseDeadlineRow({ driveMon }: { driveMon: string }) {
  const { me } = useMe();
  const isAdmin = me?.role === 'admin';
  const [rows, setRows] = useState<Row[] | null>(null);
  const [edit, setEdit] = useState<Record<string, string> | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const [preview, setPreview] = useState(false);

  useEffect(() => {
    if (!driveMon) return;
    let alive = true;
    api.listCloseDeadlines({ mon: driveMon }).then((r) => { if (alive) setRows(r.rows as Row[]); }).catch(() => { if (alive) setRows([]); });
    return () => { alive = false; };
  }, [driveMon, nonce]);

  if (!driveMon || rows === null) return null;
  const of = (k: string) => rows.find((r) => r.kind === k);

  async function save() {
    if (!edit) return;
    setBusy(true);
    setErr(null);
    try {
      for (const k of KINDS) {
        const v = (edit[k] ?? '').trim();
        if (v === (of(k)?.deadline ?? '')) continue;
        await api.saveCloseDeadline({ mon: driveMon, kind: k, deadline: v || null });
      }
      setEdit(null);
      setNonce((n) => n + 1);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <span className="cursor-help font-bold" title="기한 다음 날 0시부터 그 정산월 쓰기가 막힙니다(관리자·회계 제외 · 회계는 알림만). 기한 당일 13시·15시에 공지 팝업이 뜹니다. 매달 1일 영업일 기준으로 자동으로 들어갑니다.">
        마감 기한
      </span>
      {!edit && KINDS.map((k) => {
        const r = of(k);
        return (
          <span key={k} className={`rounded px-1.5 py-0.5 ${r ? (r.closed ? 'bg-slate-200 text-ink-soft' : 'bg-amber-100') : 'text-ink-faint'}`} title={r?.note ?? '기한 없음 = 열림'}>
            {k} {r ? `${md(r.deadline)}${r.closed ? ' 지남' : ''}` : '—'}
          </span>
        );
      })}
      {!edit && <LatePayButton driveMon={driveMon} />}
      {!edit && isAdmin && rows.length > 0 && (
        <button className="btn-ghost px-2 py-0.5 text-xs" onClick={() => setPreview(true)}>
          팝업 미리보기
        </button>
      )}
      {preview && <DeadlineNotice preview rows={KINDS.map(of).filter((r): r is Row => !!r)} onClose={() => setPreview(false)} />}
      {!edit && isAdmin && (
        <button className="btn-ghost px-2 py-0.5 text-xs" onClick={() => setEdit(Object.fromEntries(KINDS.map((k) => [k, of(k)?.deadline ?? ''])))}>
          기한 조정
        </button>
      )}
      {edit && (
        <>
          {KINDS.map((k) => (
            <label key={k} className="flex items-center gap-1">
              {k}
              <input type="date" className="field px-1 py-0.5 text-xs" value={edit[k] ?? ''} onChange={(e) => setEdit({ ...edit, [k]: e.target.value })} />
            </label>
          ))}
          <span className="text-ink-faint">비우면 그 구분은 막지 않습니다</span>
          <button className="btn-primary px-2 py-0.5 text-xs" disabled={busy} onClick={save}>{busy ? '…' : '저장'}</button>
          <button className="btn-ghost px-2 py-0.5 text-xs" onClick={() => setEdit(null)}>취소</button>
        </>
      )}
      {err && <span className="text-red-600">{err}</span>}
    </div>
  );
}
